import React, { useState, useContext, useEffect } from 'react';
import {
	Button,
	Card,
	CardBody,
	Input,
	Table,
	TableCell,
	TableContainer,
	TableHeader,
} from '@windmill/react-ui';
import ExportDataXlsx from 'components/common/ExportDataXlsx';
import DataWrapper from './DataWrapper';
import NotFound from 'components/table/NotFound';
import PageTitle from 'components/Typography/PageTitle';
import useAsync from 'hooks/useAsync';
import useFilter from 'hooks/useFilter';
import VendorServices from 'services/VendorServices';
import MainDrawer from 'components/drawer/MainDrawer';
import DeleteModal from 'components/modal/DeleteModal';
import useToggleDrawer from 'hooks/useToggleDrawer';
import { SidebarContext } from 'context/SidebarContext';
import Somethingwentwrong from 'pages/Somethingwentwrong';
import Loader from 'components/form/Loader';
import CityVendorRequestTable from 'components/vendor/CityVendorRequestTable';
import sidebar from 'routes/sidebar';
import { FiRefreshCcw } from 'react-icons/fi';
import CityVendorRequestServices from 'services/CityVendorServices';
import SelectSingleCityOnly from 'components/form/SelectSingleCityOnly';

const CityVendorRequest = () => {
	const adminInfo = localStorage.getItem('adminInfo')
		? JSON.parse(localStorage.getItem('adminInfo'))
		: null;

	const { functionLoading, setIsUpdate } = useContext(SidebarContext);
	const { title, serviceId } = useToggleDrawer();

	//   Selected City state
	const [selectedCity, setSelectedCity] = useState('');
	const [cityName, setCityName] = useState('');
	const [data, setData] = useState({ status: false, data: [] });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const [itemsPerPage, setItemsPerPage] = useState(10);

	const {
		cityVendorRequestRef,
		dataTable,
		serviceData,
		handleSubmitCityVendorRequest,
		currentPage,
	} = useFilter(Array.isArray(data?.data) ? data?.data : [], itemsPerPage);

	//   Fetch vendor list based on city selection
	const fetchCityVendorRequests = async (cityId = '') => {
	//	console.log(cityId);
		try {
			setLoading(true);
			const res = await CityVendorRequestServices.getAllCityVendorRequests({
				city_id: cityId || '', // send in body
			});
			setData(res);
			setError(null);
		} catch (err) {
			console.error('Error fetching vendor requests:', err);
			setError(err);
		} finally {
			setLoading(false);
		}
	};

	//  Load all initially
	useEffect(() => {
		fetchCityVendorRequests();
	}, []);

	const updatedData = Array.isArray(data?.data)
		? data.data.map((item, index) => ({
				'SI.No': index + 1,
				'Company Name': item.company_name,
				'Person Name': item.full_name,
				City: item.city_name,
				State: item.state_name,
				Address: item.address,
				'Employees Count': item.no_of_employees,
				'Fleet Count': item.no_of_fleet,
				'Added Date': item.added_date,
				'Company Age': item.company_age,
				'Contact Number': item.contact_no,
				'Vendor Reason': item.vendor_reason,
		  }))
		: [];

	const isAdmin = adminInfo?.id === 1;
	const hideView = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'city vendors' && perm.view === 1
		  );

	const hideExport = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'city vendors' && perm.export === 1
		  );

const handleRefresh = () => {
  // Clear filters
  setSelectedCity('');
  setCityName('');
  
  if (cityVendorRequestRef.current) {
    cityVendorRequestRef.current.value = ''; // Clear search input
  }

  // Fetch all data (no filter)
  fetchCityVendorRequests();
};


	return (
		<>
			{hideView && (
				<>
					<DeleteModal id={serviceId} title={title} />
					<MainDrawer />
					<PageTitle sidebar={sidebar().sidebar} />

					<Card
						className="min-w-0 shadow-xs bg-white dark:bg-gray-800 mb-5"
						style={{ overflow: 'visible' }}
					>
						<CardBody>
							<div className="flex items-center justify-between gap-4 flex-wrap">
								{/* 🔍 Search + City + Button */}
								<div className="flex items-center gap-4 w-full max-w-4xl">
									{/* 🔍 Search Input */}
									<Input
										ref={cityVendorRequestRef}
										className="border h-10 text-sm w-[280px] focus:outline-none bg-gray-100 border-transparent focus:bg-white"
										type="search"
										name="search"
										autoComplete="off"
										placeholder="Quick Search"
										onChange={handleSubmitCityVendorRequest}
									/>

									{/* City Dropdown (wider + same height) */}
									<div
										style={{
											width: '380px', // ⬅ Increased width
											position: 'relative',
											zIndex: 50,
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<SelectSingleCityOnly
											name="city"
											label=""
											selectedCity={selectedCity}
											handleCityChange={setSelectedCity}
											handleCityChangeName={setCityName}
											register={() => ({})}
										/>
									</div>

									<Button
										className="rounded-md h-10 px-8"
										onClick={() => fetchCityVendorRequests(selectedCity)} // ⬅ Trigger API call on click
									>
										Search
									</Button>
								</div>

								<div className="flex items-center gap-3">
									{hideExport && (
										<ExportDataXlsx
											exportDataXlsx={updatedData}
											fileNames="Vendor Request"
										/>
									)}
									<FiRefreshCcw
										onClick={handleRefresh}
										cursor="pointer"
										className="text-xl hover:text-blue-600 transition"
									/>
								</div>
							</div>
						</CardBody>
					</Card>

					{functionLoading && <Loader message="Please wait..." />}

					<DataWrapper loading={loading} error={error} status={data.status}>
						{serviceData?.length !== 0 ? (
							<TableContainer>
								<div
									className="overflow-y-scroll"
									style={{ maxHeight: '67vh', scrollbarWidth: 'thin' }}
								>
									<Table>
										<TableHeader className="custom-table-header capitalize z-10 text-center sticky top-0 text-sm">
											<tr>
												<TableCell>Sl.No.</TableCell>
												<TableCell>Company Name</TableCell>
												<TableCell>Name</TableCell>
												<TableCell>City</TableCell>
												<TableCell>State</TableCell>
												<TableCell>Action</TableCell>
											</tr>
										</TableHeader>
										<CityVendorRequestTable
											vendor={dataTable}
											perpage={itemsPerPage}
											currentPage={currentPage}
										/>
									</Table>
								</div>
							</TableContainer>
						) : (
							<>
								{data.status !== true ? (
									<Somethingwentwrong title="Something Went Wrong, Try After Sometime" />
								) : (
									<NotFound title="There are no Vendors." />
								)}
							</>
						)}
					</DataWrapper>
				</>
			)}
		</>
	);
};

export default CityVendorRequest;
